import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight, Globe } from 'lucide-react';
import { contentEs } from '../data/contentEs.js';
import { contentEn } from '../data/contentEn.js';

interface MarketingHeaderProps {
  isEn?: boolean;
}

export const MarketingHeader: React.FC<MarketingHeaderProps> = ({ isEn = false }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const c = isEn ? contentEn : contentEs;

  const getSwitchedPath = () => {
    const p = location.pathname;
    if (isEn) {
      if (p.includes('/business')) return '/empresas';
      if (p.includes('/locations')) return '/sucursales';
      if (p.includes('/invoicing')) return '/facturacion';
      return '/';
    } else {
      if (p.includes('/empresas')) return '/en/business';
      if (p.includes('/sucursales')) return '/en/locations';
      if (p.includes('/facturacion')) return '/en/invoicing';
      return '/en';
    }
  };

  const homePath = isEn ? '/en' : '/';
  const businessPath = isEn ? '/en/business' : '/empresas';
  const operationPath = `${businessPath}#operacion`;
  const locationsPath = isEn ? '/en/locations' : '/sucursales';
  const invoicePath = isEn ? '/en/invoicing' : '/facturacion';
  const contactPath = `${businessPath}#contacto`;

  const isBusiness = location.pathname.includes('empresas') || location.pathname.includes('business');
  const isLocations = location.pathname.includes('sucursales') || location.pathname.includes('locations');
  const isInvoice = location.pathname.includes('facturacion') || location.pathname.includes('invoicing');

  return (
    <header className="sticky top-0 z-50 bg-[#FBF9F5]/95 backdrop-blur-md border-b border-[#E8E4DC] transition-all">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24 sm:h-28">
          
          {/* Logotipo Grande Limpio sin texto y sin ribete */}
          <Link
            to={homePath}
            className="flex-shrink-0 flex items-center py-2 mr-8 lg:mr-14 xl:mr-20 group"
            aria-label="Doña Blanca"
          >
            <img
              src="/website/dona_blanca_logo.png"
              alt="Doña Blanca"
              className="h-20 w-20 sm:h-24 sm:w-24 md:h-26 md:w-26 object-contain transition-transform duration-200 group-hover:scale-105 drop-shadow-md"
            />
          </Link>

          {/* Menú de Navegación */}
          <nav className="hidden lg:flex items-center gap-7 xl:gap-8">
            <Link
              to={businessPath}
              className={`text-xs xl:text-sm font-heading font-extrabold tracking-wider uppercase transition-colors duration-150 ${
                isBusiness
                  ? 'text-[#C2821E] border-b-2 border-[#C2821E] pb-0.5'
                  : 'text-[#001557] hover:text-[#C2821E]'
              }`}
            >
              {c.nav.business}
            </Link>

            <a
              href={operationPath}
              className="text-xs xl:text-sm font-heading font-extrabold tracking-wider uppercase text-[#001557] hover:text-[#C2821E] transition-colors duration-150"
            >
              {c.nav.operation}
            </a>

            <Link
              to={locationsPath}
              className={`text-xs xl:text-sm font-heading font-extrabold tracking-wider uppercase transition-colors duration-150 ${
                isLocations
                  ? 'text-[#C2821E] border-b-2 border-[#C2821E] pb-0.5'
                  : 'text-[#001557] hover:text-[#C2821E]'
              }`}
            >
              {c.nav.locations}
            </Link>

            <Link
              to={invoicePath}
              className={`text-xs xl:text-sm font-heading font-extrabold tracking-wider uppercase transition-colors duration-150 ${
                isInvoice
                  ? 'text-[#C2821E] border-b-2 border-[#C2821E] pb-0.5'
                  : 'text-[#001557] hover:text-[#C2821E]'
              }`}
            >
              {c.nav.invoice}
            </Link>

            <div className="h-5 w-[1px] bg-[#D1D5DB] mx-1" />

            {/* Selector de idioma */}
            <Link
              to={getSwitchedPath()}
              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#001557] px-2.5 py-1 rounded border border-[#D1D5DB] hover:bg-[#FAF8F5] transition-colors"
              title={isEn ? 'Cambiar a Español' : 'Switch to English'}
            >
              <Globe className="w-3.5 h-3.5 text-[#001557]" />
              <span>{c.nav.switchLang}</span>
            </Link>

            {/* CTA Cotizar */}
            <a
              href={contactPath}
              className="inline-flex items-center gap-2 bg-[#001557] hover:bg-[#11255E] text-white text-xs xl:text-sm font-bold uppercase tracking-wider px-5 py-2.5 rounded-lg shadow-sm hover:shadow transition-all duration-200"
            >
              <span>{c.nav.cta}</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </nav>

          {/* Mobile Actions */}
          <div className="flex items-center gap-3 lg:hidden">
            <Link
              to={getSwitchedPath()}
              className="inline-flex items-center gap-1 text-xs font-bold uppercase text-[#001557] px-2 py-1 rounded border border-[#D1D5DB]"
            >
              <Globe className="w-3 h-3 text-[#001557]" />
              <span>{c.nav.switchLang}</span>
            </Link>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-md text-[#001557] hover:bg-[#FAF8F5] transition-colors"
              aria-label="Abrir menú"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

        </div>
      </div>

      {/* Menú Móvil Desplegable */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-[#E8E4DC] bg-[#FAF8F5] px-4 pt-3 pb-6 space-y-3">
          <Link
            to={businessPath}
            onClick={() => setMobileMenuOpen(false)}
            className="block px-3 py-2 text-sm font-heading font-bold uppercase text-[#001557] rounded-md hover:bg-white"
          >
            {c.nav.business}
          </Link>
          <a
            href={operationPath}
            onClick={() => setMobileMenuOpen(false)}
            className="block px-3 py-2 text-sm font-heading font-bold uppercase text-[#001557] rounded-md hover:bg-white"
          >
            {c.nav.operation}
          </a>
          <Link
            to={locationsPath}
            onClick={() => setMobileMenuOpen(false)}
            className="block px-3 py-2 text-sm font-heading font-bold uppercase text-[#001557] rounded-md hover:bg-white"
          >
            {c.nav.locations}
          </Link>
          <Link
            to={invoicePath}
            onClick={() => setMobileMenuOpen(false)}
            className="block px-3 py-2 text-sm font-heading font-bold uppercase text-[#001557] rounded-md hover:bg-white"
          >
            {c.nav.invoice}
          </Link>
          <div className="pt-2">
            <a
              href={contactPath}
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-center gap-2 w-full bg-[#001557] text-white text-sm font-heading font-bold uppercase py-3 px-4 rounded-lg shadow"
            >
              <span>{c.nav.cta}</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
